/**
 * B090050 受払照会
 */

import { Column, Query } from "@material-table/core";
import { Box } from "@mui/material";
import {
  B000140UpdshainNm,
  B000190SokoNmr,
  B000200JanshohinNmr,
  B000200ShohinNmr,
  B090050Find,
  B090050FindRequest,
  B090050GetExcel,
  catchFileError,
  catchRestError,
} from "clients/rest";
import {
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  SText,
  SCodeSelect,
  SFileButton,
  msgState,
} from "components";
import { getClientError, getClientMessage } from "messages";
import { SCode, B090050UkeharaiKbn } from "messages/codes";
import { labels } from "messages/labels";
import { B000140, B000190, B000200 } from "pages";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, download, endOfMonth, mapIds, startOfMonth } from "services";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  torihikiYmdFrom: string;
  torihikiYmdTo: string;
  sokoCd: string;
  sokoNmr: string;
  shohinCd: string;
  shohinNmr: string;
  janCd: string;
  janshohinNmr: string;
  updDateFrom: string;
  updhourFrom: string;
  updminuteFrom: string;
  updDateTo: string;
  updhourTo: string;
  updminuteTo: string;
  updshainCd: string;
  updshainNm: string;
  ukeharaiKbn: SCode;
  // 倉庫種別指定用パラメータ
  sokoshubetsuList: string[];
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  crtDate: string;
  ukeharaiKbnNm: string;
  ukeiresu: number;
  haraidashisu: number;
  hikiatesu: number;
  denpyoNo: number;
  torihikiCd: string;
  torihikishitenCd: string;
  torihikiNmr: string;
  torihikiYmdView: string;
  aitesakisokoCd: string;
  aitesakisokoNmr: string;
  shainNm: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  torihikiYmdFrom: startOfMonth(date()),
  torihikiYmdTo: endOfMonth(date()),
  sokoCd: "",
  sokoNmr: "",
  shohinCd: "",
  shohinNmr: "",
  janCd: "",
  janshohinNmr: "",
  updDateFrom: "",
  updhourFrom: "",
  updminuteFrom: "",
  updDateTo: "",
  updhourTo: "",
  updminuteTo: "",
  updshainCd: "",
  updshainNm: "",
  ukeharaiKbn: null,
  sokoshubetsuList: ["1", "2", "3", "4", "5"],
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "4rem", align: "right" },
  { field: "crtDate", title: "更新日時", width: "15rem" },
  { field: "ukeharaiKbn", hidden: true },
  {
    field: "ukeharaiKbnNm",
    title: "受払区分",
    width: "10rem",
  },
  {
    field: "ukeiresu",
    title: "受入数　",
    width: "6rem",
    align: "right",
    render: (data) => data.ukeiresu?.toLocaleString(),
  },
  {
    field: "haraidashisu",
    title: "払出数　",
    width: "6rem",
    align: "right",
    render: (data) => data.haraidashisu?.toLocaleString(),
  },
  {
    field: "hikiatesu",
    title: "引当数　",
    width: "6rem",
    align: "right",
    render: (data) => data.hikiatesu?.toLocaleString(),
  },
  { field: "denpyoNo", title: "伝票No.", width: "10rem" },
  { field: "torihikiCd", title: "取引先コード", width: "8rem" },
  { field: "torihikishitenCd", title: "支店コード", width: "6rem" },
  { field: "torihikiNmr", title: "取引先名", width: "10rem" },
  { field: "torihikiYmdView", title: "取引日", width: "10rem" },
  { field: "aitesakisokoCd", title: "相手先倉庫コード", width: "8rem" },
  { field: "aitesakisokoNmr", title: "相手先倉庫名", width: "10rem" },
  { field: "shainNm", title: "更新者", width: "10rem" },
];

const focusId = "B090050";

export const B090050 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // テーブルの値取得Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // Excel出力ボタン活性制御
  const [disabledExcelButton, setDisabledExcelButton] = useState(true);
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  // 初期表示イベント
  useEffect(() => {
    focus(1);
  });
  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    const fields = methods.getValues();
    // 更新時(From)チェック
    if (fields.updhourFrom) {
      if (Number(fields.updhourFrom) >= 24 || Number(fields.updhourFrom) < 0) {
        setMsg(getClientError("B090050_001"));
        return;
      }
    }

    // 更新分(From)チェック
    if (fields.updminuteFrom) {
      if (
        Number(fields.updminuteFrom) >= 60 ||
        Number(fields.updminuteFrom) < 0
      ) {
        setMsg(getClientError("B090050_002"));
        return;
      }
    }

    // 更新時(To)チェック
    if (fields.updhourTo) {
      if (Number(fields.updhourTo) >= 24 || Number(fields.updhourTo) < 0) {
        setMsg(getClientError("B090050_003"));
        return;
      }
    }

    // 更新分(To)チェック
    if (fields.updminuteTo) {
      if (Number(fields.updminuteTo) >= 60 || Number(fields.updminuteTo) < 0) {
        setMsg(getClientError("B090050_004"));
        return;
      }
    }

    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // EXCEL出力ボタン非活性
    setDisabledExcelButton(true);
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();
      // 出力順の設定
      let order = ["crtDate,asc"];

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータ作成
      const param: B090050FindRequest = {
        torihikiYmdFrom: fields.torihikiYmdFrom!,
        torihikiYmdTo: fields.torihikiYmdTo!,
        sokoCd: fields.sokoCd!,
        shohinCd: fields.shohinCd!,
        janCd: fields.janCd,
        updDateFrom: fields.updDateFrom,
        updhourFrom: fields.updhourFrom,
        updminuteFrom: fields.updminuteFrom,
        updDateTo: fields.updDateTo,
        updhourTo: fields.updhourTo,
        updminuteTo: fields.updminuteTo,
        updshainCd: fields.updshainCd,
        ukeharaiKbn: fields.ukeharaiKbn?.value!,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: order,
      };
      // API呼出し
      B090050Find(param)
        // 正常値取得
        .then((value) => {
          if (value.data.totalElements === 0) {
            setMsg(getClientError("B090050_005"));
            setDisabledExcelButton(true);
          } else {
            setDisabledExcelButton(false);
          }
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        .catch((reason) => {
          setDisabledExcelButton(true);
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // Excel出力ボタンクリックイベント
  const handleClickExcelButton = () => {
    // 入力フォームの結果取得
    const fields = methods.getValues();
    // 更新時(From)チェック
    if (fields.updhourFrom) {
      if (Number(fields.updhourFrom) >= 24 || Number(fields.updhourFrom) < 0) {
        setMsg(getClientError("B090050_001"));
        return;
      }
    }

    // 更新分(From)チェック
    if (fields.updminuteFrom) {
      if (
        Number(fields.updminuteFrom) >= 60 ||
        Number(fields.updminuteFrom) < 0
      ) {
        setMsg(getClientError("B090050_002"));
        return;
      }
    }

    // 更新時(To)チェック
    if (fields.updhourTo) {
      if (Number(fields.updhourTo) >= 24 || Number(fields.updhourTo) < 0) {
        setMsg(getClientError("B090050_003"));
        return;
      }
    }

    // 更新分(To)チェック
    if (fields.updminuteTo) {
      if (Number(fields.updminuteTo) >= 60 || Number(fields.updminuteTo) < 0) {
        setMsg(getClientError("B090050_004"));
        return;
      }
    }

    // 出力順の設定
    let order = ["crtDate,asc"];
    // Excel出力パラメータ作成
    const param: B090050FindRequest = {
      torihikiYmdFrom: fields.torihikiYmdFrom!,
      torihikiYmdTo: fields.torihikiYmdTo!,
      sokoCd: fields.sokoCd!,
      shohinCd: fields.shohinCd!,
      janCd: fields.janCd,
      updDateFrom: fields.updDateFrom,
      updhourFrom: fields.updhourFrom,
      updminuteFrom: fields.updminuteFrom,
      updDateTo: fields.updDateTo,
      updhourTo: fields.updhourTo,
      updminuteTo: fields.updminuteTo,
      updshainCd: fields.updshainCd,
      ukeharaiKbn: fields.ukeharaiKbn?.value!,
      // ソートの設定
      sort: order,
    };
    B090050GetExcel(param)
      .then((response) => {
        download(response);
        setMsg(getClientMessage("B090050_001"));
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="受払照会"
        gridTemplateColumns="6rem 5rem 5rem 2rem 10rem 13rem 6rem 10rem 2rem 1rem 2rem 1rem 10rem 2rem 1rem 2rem"
      >
        <SLabel required>取引日</SLabel>
        <SDateField
          label="取引日(From)"
          name="torihikiYmdFrom"
          required
          gridColumn="span 2"
          {...register(1)}
        />
        <SText>～</SText>
        <SDateField
          label="取引日(To)"
          name="torihikiYmdTo"
          required
          {...register(2)}
        />
        <Box />
        <SLabel>更新日時</SLabel>
        <SDateField
          label="更新日時(日付)(From)"
          name="updDateFrom"
          {...register(6)}
        />
        <SNumberField
          label="更新日時(時)(From)"
          name="updhourFrom"
          size={2}
          align="center"
          {...register(7)}
        />
        <SText>：</SText>
        <SNumberField
          label="更新日時(分)(From)"
          name="updminuteFrom"
          size={2}
          align="center"
          {...register(8)}
        />
        <SText>～</SText>
        <SDateField
          label="更新日時(日付)(To)"
          name="updDateTo"
          {...register(9)}
        />
        <SNumberField
          label="更新日時(時)(To)"
          name="updhourTo"
          size={2}
          align="center"
          {...register(10)}
        />
        <SText>：</SText>
        <SNumberField
          label="更新日時(分)(To)"
          name="updminuteTo"
          size={2}
          align="center"
          {...register(11)}
        />
        <SLabel required>倉庫</SLabel>
        <STextField
          label="倉庫コード"
          name="sokoCd"
          size={3}
          required
          dialog={
            <B000190 sokoshubetsuList={methods.watch("sokoshubetsuList")} />
          }
          handleSubmit={(data: any) => {
            methods.setValue("sokoCd", data.sokoCd);
            methods.setValue("sokoNmr", data.sokoNmr);
          }}
          gridColumn="span 2"
          onBlur={() => B000190SokoNmr(methods, navigate, setMsg)}
          {...register(3)}
        />
        <STextField
          label="倉庫名"
          name="sokoNmr"
          required
          disabled
          gridColumn="span 2"
        />
        <Box />
        <SLabel>更新者</SLabel>
        <SNumberField
          label="更新社員コード"
          name="updshainCd"
          code
          size={7}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("updshainCd", data.shainCd);
            methods.setValue("updshainNm", data.shainNm);
          }}
          onBlur={() => B000140UpdshainNm(methods, navigate, setMsg)}
          {...register(12)}
        />
        <STextField
          label="更新社員名"
          name="updshainNm"
          disabled
          gridColumn="span 8"
        />
        <SLabel required>商品</SLabel>
        <SNumberField
          label="商品コード"
          name="shohinCd"
          code
          size={10}
          gridColumn="span 2"
          required
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("shohinCd", data.shohinCd);
            methods.setValue("shohinNmr", data.shohinNmr);
          }}
          onBlur={() => B000200ShohinNmr(methods, navigate, setMsg)}
          {...register(4)}
        />
        <STextField
          label="商品名"
          name="shohinNmr"
          disabled
          gridColumn="span 3"
        />

        <SLabel>受払区分</SLabel>
        <SCodeSelect
          label="受払区分"
          name="ukeharaiKbn"
          options={B090050UkeharaiKbn}
          empty
          gridColumn="span 5"
          {...register(13)}
        />
        <Box gridColumn="span 4" />
        <SLabel>JANコード</SLabel>
        <SNumberField
          label="JANコード"
          name="janCd"
          code
          size={13}
          gridColumn="span 2"
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("janCd", data.janCd);
            methods.setValue("janshohinNmr", data.janshohinNmr);
          }}
          onBlur={() => B000200JanshohinNmr(methods, navigate, setMsg)}
          {...register(5)}
        />
        <STextField
          label="JAN商品名"
          name="janshohinNmr"
          disabled
          gridColumn="span 3"
        />
        <Box gridColumn="span 7" />
        <SNormalButton
          label={labels.buttons.search}
          gridColumn="span 3"
          onClick={handleClickSearchButton}
          align="right"
          {...register(14)}
        />
      </SFrame>

      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          width={1250}
        />
      </SFrame>

      <SFrame>
        <SFileButton
          label={labels.buttons.excel}
          onClick={handleClickExcelButton}
          disabled={disabledExcelButton}
          align="right"
          {...register(17)}
        />
      </SFrame>
    </FormProvider>
  );
};
